import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { Badge, Carousel, Modal } from '@pella/sash-framework';
import { Image } from '../../..';
import { isValidArray } from '../../../utilities/validations';

const GalleryModal = ({ isOpen, onClose, images, activeImage, articleTitle }) => {
	const isDesktop = useMediaQuery({ minWidth: 1280 });
	const [activeSlideIndex, setActiveSlideIndex] = useState(0);

	useEffect(() => {
		const imageIndex = images.findIndex((image) => (isValidArray(image) ? image[0].image.url === activeImage || image[1].image.url === activeImage : image.image.url === activeImage));

		if (activeImage) {
			setActiveSlideIndex(imageIndex);
		}
	}, [activeImage, images]);

	const onSlideChange = (index) => setActiveSlideIndex(index);

	if (!isValidArray(images)) return null;

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			width={1152}
			className='!my-0 !py-0 px-4 xl:max-h-[792px] xl:px-5 [&>button>span]:bg-white [&>button>span]:text-5xl/none [&>button>span]:font-light [&>button>span]:text-black [&>button]:top-1 [&>button]:h-auto [&>button]:w-auto'
			align='center'
			data-testid='gallery-modal'
		>
			<Modal.Header
				headingLevel={3}
				className='!border-0 !px-0 !pb-0 !pt-4'
			>
				Project Gallery{articleTitle ? `: ${articleTitle}` : ''}
			</Modal.Header>

			<Modal.Content className='max-h-[90vh] overflow-auto !p-4 md:!px-[52px] xl:grid xl:grid-cols-[1fr_80px] xl:!px-0'>
				<div className='h-[calc(90vh-220px)] max-h-[575px] xl:h-[calc(90vh-50px)] xl:max-h-[calc(792px-64px)] xl:pl-12 xl:pr-14'>
					<Carousel
						scrollTo={activeSlideIndex}
						wrapperClassName='h-full flex flex-col [&>div]:flex-1 [&>p]:flex-none [&>p]:my-2'
						prevButtonClass='md:-left-[52px] xl:-left-12'
						nextButtonClass='md:-right-[52px] xl:-right-12'
						className='h-full'
						showSlideCount
						showArrows
					>
						{images.map((image, index) => (
							<div
								key={index}
								className='flex h-full flex-col'
								data-active={activeSlideIndex === index}
							>
								<div className={`flex max-h-[calc(100%-70px)] flex-1 items-center justify-center bg-gray-70 md:max-h-[calc(100%-40px)]`}>
									<div className='flex max-h-full gap-x-2.5'>
										{!isValidArray(image) && (
											<div className='flex-1 text-center'>
												<Image
													data={image}
													className='max-h-full [&_img]:!object-contain'
												/>
											</div>
										)}

										{isValidArray(image) &&
											image.map((baImage, imgIndex) => (
												<div
													key={imgIndex}
													className='relative flex-1'
												>
													<Badge
														color='black'
														className='absolute left-1 top-1 z-3 md:left-2 md:top-2 xl:left-3 xl:top-3'
													>
														{imgIndex === 0 ? 'Before' : 'After'}
													</Badge>
													<Image
														data={baImage}
														className='max-h-full [&_img]:w-auto [&_img]:!object-contain'
													/>
												</div>
											))}
									</div>
								</div>

								{image.image_title && <p className='text-caption my-2 flex-none'>{image.image_title}</p>}
							</div>
						))}
					</Carousel>
				</div>

				<div className='xl:col-start-2 xl:h-[calc(90vh-50px)] xl:max-h-[calc(792px-64px)]'>
					<Carousel
						key={isDesktop}
						scrollTo={activeSlideIndex}
						slidePadding={8}
						className='relative xl:h-full [&>li]:flex-none'
						wrapperClassName='xl:h-full xl:[&>div]:h-full'
						prevButtonClass='!border-0 !bg-transparent !w-6 xl:!w-full !h-full xl:!h-6 !left-0 !top-0 -indent-full from-white to-transparent bg-gradient-to-r xl:bg-gradient-to-b !mt-0 !shadow-none'
						nextButtonClass='!border-0 !bg-transparent !w-6 xl:!w-full !h-full xl:!h-6 !right-0 !bottom-0 !top-auto -indent-full from-white to-transparent bg-gradient-to-l xl:bg-gradient-to-t !mt-0 !shadow-none'
						center={false}
						vertical={isDesktop}
						showArrows
					>
						{images.map((image, index) => (
							<button
								aria-label={`Change to slide ${index + 1}`}
								className={`block xl:h-20 xl:w-20`}
								onClick={() => onSlideChange(index)}
								data-active={activeSlideIndex === index}
								key={index}
							>
								<Image
									data={isValidArray(image) ? image[1] : image}
									className={`!block aspect-square w-[60px] cursor-pointer border xl:w-20 ${activeSlideIndex === index ? 'border-black shadow-[0px_2px_5px_rgba(26,26,26,0.3)]' : 'border-transparent opacity-80'}`}
								/>
							</button>
						))}
					</Carousel>
				</div>
			</Modal.Content>
		</Modal>
	);
};

GalleryModal.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func,
	images: PropTypes.array,
	activeImage: PropTypes.string,
	articleTitle: PropTypes.string,
};

export default GalleryModal;
